import { useState, useEffect } from 'react';
import { saveContent, loadContent, deleteArchive } from '../services/contentStorage';

const BACKEND = process.env.REACT_APP_MEDIA_STUDIO_API_URL || 'https://strategic-honesty-media-studio-production.up.railway.app';
const API_KEY = process.env.REACT_APP_API_KEY || '';

const PLAT_META = {
  li:  { label: 'LinkedIn',       icon: '💼' },
  tt:  { label: 'TikTok',         icon: '🎵' },
  yt:  { label: 'YouTube Shorts', icon: '▶️' },
  ig:  { label: 'Instagram',      icon: '📸' },
  fb:  { label: 'Facebook',       icon: '👥' },
  th:  { label: 'Threads',        icon: '🧵' },
  bs:  { label: 'Bluesky',        icon: '🦋' },
  ms:  { label: 'Mastodon',       icon: '🐘' },
  tw:  { label: 'X / Twitter',    icon: '𝕏'  },
  pi:  { label: 'Pinterest',      icon: '📌' },
};

const C = {
  text: '#0f172a', muted: '#64748b', border: '#e2e8f0',
  card: '#fff', bg: '#f8fafc', gold: '#BA7517', goldLight: '#FEF9EC',
  navy: '#1E293B', purple: '#7c3aed', purpleLight: '#F5F0FF',
  green: '#166534', greenLight: '#dcfce7',
};

function getFirstLine(content) {
  if (!content) return '';
  const SKIP = /^(tiktok script|youtube script|instagram|facebook|linkedin|threads|bluesky|mastodon|x \/|visual cue|hook|problem|story|cta|outro|post \d|caption|\[\d)/i;
  const lines = content
    .replace(/\*\*/g, '')
    .replace(/\[.*?\]/g, '')
    .split('\n')
    .map(l => l.replace(/^#+\s*/, '').trim())
    .filter(l => l.length > 8 && !SKIP.test(l));
  const line = lines[0] || '';
  return line.slice(0, 80) + (line.length > 80 ? '…' : '');
}

function IdeaDetail({ idea, onClose, onRestored, setMainTab }) {
  const [cards, setCards]   = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [cardsRes, assetsRes] = await Promise.all([
          fetch(`${BACKEND}/api/content/cards?ideaId=${idea.id}`, { headers: { 'x-api-key': API_KEY } }),
          fetch(`${BACKEND}/api/assets?q=${encodeURIComponent(idea.title?.slice(0,30)||'')}`, { headers: { 'x-api-key': API_KEY } }),
        ]);
        const cardsData  = await cardsRes.json();
        const assetsData = await assetsRes.json();
        setCards(cardsData.cards || []);
        setAssets(Array.isArray(assetsData) ? assetsData.filter(a => a.id !== 'test-001') : []);
      } catch(e) { console.warn('[Archive] load failed', e); }
      finally { setLoading(false); }
    }
    load();
  }, [idea.id]);

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '16px 18px', marginBottom: 12 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 3 }}>{idea.title}</div>
          <div style={{ fontSize: 11, color: C.muted }}>
            🎉 Published {idea.publishedAt ? new Date(idea.publishedAt).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' }) : '—'}
          </div>
          {idea.findingRef && <div style={{ fontSize: 11, color: C.gold, fontStyle: 'italic', marginTop: 3 }}>From research: {idea.findingRef}</div>}
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 16, color: C.muted, cursor: 'pointer', padding: '0 4px' }}>✕</button>
      </div>

      {loading ? (
        <div style={{ fontSize: 12, color: C.muted, padding: '12px 0' }}>Loading content...</div>
      ) : (
        <>
          {/* Platform cards */}
          {cards.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 8 }}>Platforms Generated</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {cards.map(card => {
                  const meta = PLAT_META[card.plat_id] || { label: card.plat_id, icon: '📄' };
                  const firstLine = getFirstLine(card.content);
                  return (
                    <div key={card.plat_id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                      <span style={{ width: 18, flexShrink: 0 }}>{meta.icon}</span>
                      <span style={{ width: 110, color: C.muted, fontWeight: 600, flexShrink: 0 }}>{meta.label}</span>
                      <span style={{ color: C.text, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>"{firstLine}"</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Assets */}
          {assets.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 8 }}>Assets Created</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {assets.map(asset => (
                  <div key={asset.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                    <span>{asset.type === 'video' ? '🎬' : '🖼'}</span>
                    <span style={{ color: C.text, flex: 1, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                      "{asset.title?.slice(0, 60) || 'Untitled'}"
                    </span>
                    <a href={asset.url} target="_blank" rel="noopener noreferrer"
                      style={{ color: C.gold, fontSize: 11, fontWeight: 600, textDecoration: 'none', flexShrink: 0 }}>↗ Open</a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {cards.length === 0 && assets.length === 0 && (
            <div style={{ fontSize: 12, color: C.muted }}>No generated content found for this idea.</div>
          )}
        </>
      )}

      {/* Restore button */}
      <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
        <button disabled={restoring} onClick={async () => {
          setRestoring(true);
          try {
            const delRes = await deleteArchive(idea.archiveId);
            if (!delRes?.success) { alert('Restore failed \u2014 could not remove archive record. Please try again.'); setRestoring(false); return; }
            const currentIdeas = await loadContent('ideas');
            const restored = { ...idea, id: idea.ideaId || idea.id, status: 'review', restoredAt: new Date().toISOString() };
            const merged = [restored, ...(currentIdeas || []).filter(i => i.id !== restored.id)].slice(0, 30);
            const saveRes = await saveContent('ideas', merged);
            if (!saveRes?.success && saveRes !== undefined) { /* saveContent may not return a shape; proceed regardless */ }
            onRestored?.(idea.archiveId);
            onClose();
            setMainTab?.('repurpose');
          } catch (e) {
            alert('Restore failed: ' + e.message);
            setRestoring(false);
          }
        }} style={{ padding: '6px 14px', fontSize: 12, fontWeight: 500, background: restoring ? '#f0f0f0' : C.goldLight, color: restoring ? C.muted : C.gold, border: `1px solid ${restoring ? C.border : C.gold + '44'}`, borderRadius: 8, cursor: restoring ? 'not-allowed' : 'pointer' }}>
          {restoring ? '\u21BB Restoring\u2026' : '\u21A9 Restore to Active'}
        </button>
      </div>
    </div>
  );
}

export default function ArchivePanel({ setMainTab }) {
  const [archived, setArchived] = useState([]);
  const [search, setSearch]     = useState('');
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(`${BACKEND}/api/archive`, { headers: { 'x-api-key': API_KEY } });
        const data = await res.json();
        setArchived(data.items || []);
      } catch (e) {
        console.warn('[Archive] load failed', e);
        setArchived([]);
      }
    }
    load();
  }, []);

  const filtered = archived.filter(i =>
    !search || i.title?.toLowerCase().includes(search.toLowerCase()) ||
    i.core?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', maxWidth: 780 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>📦 Archive</div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
            {archived.length} published ideas — click any to see what was generated
          </div>
        </div>
        <button onClick={() => setMainTab('repurpose')}
          style={{ padding: '6px 14px', fontSize: 12, fontWeight: 500, background: C.purple, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
          ✦ Go to Repurpose
        </button>
      </div>

      {/* Search */}
      <input value={search} onChange={e => setSearch(e.target.value)}
        placeholder="🔍 Search archived ideas..."
        style={{ width: '100%', padding: '9px 13px', fontSize: 13, border: `1px solid ${C.border}`, borderRadius: 9, marginBottom: 14, boxSizing: 'border-box', outline: 'none', color: C.text }} />

      {/* Empty state */}
      {archived.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px 20px', background: C.card, borderRadius: 12, border: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>📭</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 6 }}>No archived ideas yet</div>
          <div style={{ fontSize: 13, color: C.muted }}>Click 🎉 Published on any idea in Repurpose — it will appear here instantly.</div>
        </div>
      )}

      {/* List */}
      {filtered.map(idea => (
        <div key={idea.id}>
          {expanded === idea.id ? (
            <IdeaDetail
              idea={idea}
              onClose={() => setExpanded(null)}
              setMainTab={setMainTab}
              onRestored={archiveId => setArchived(prev => prev.filter(a => a.archiveId !== archiveId))}
            />
          ) : (
            <div onClick={() => setExpanded(idea.id)}
              style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 15px', marginBottom: 8, cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: 12, transition: 'border-color .15s' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = C.gold}
              onMouseLeave={e => e.currentTarget.style.borderColor = C.border}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 3 }}>{idea.title}</div>
                {idea.findingRef && <div style={{ fontSize: 10, color: C.gold, fontStyle: 'italic', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>From research: {idea.findingRef}</div>}
                <div style={{ fontSize: 11, color: C.muted }}>
                  🎉 Published {idea.publishedAt ? new Date(idea.publishedAt).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' }) : '—'}
                  {idea.pillars && <span style={{ marginLeft: 8, padding: '1px 6px', borderRadius: 8, background: C.purpleLight, color: C.purple }}>{idea.pillars}</span>}
                </div>
              </div>
              <span style={{ fontSize: 11, color: C.muted, flexShrink: 0, marginTop: 2 }}>View details →</span>
            </div>
          )}
        </div>
      ))}

      {filtered.length === 0 && search && (
        <div style={{ textAlign: 'center', padding: 24, color: C.muted, fontSize: 13 }}>No ideas match "{search}"</div>
      )}
    </div>
  );
}
